class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      searchQuery: "[data-query]",
      searchMessage: "[data-message]",
      input: "[data-search-input]",
      submit: 'button[type="submit"]',
      loading: "[data-spinner]",
      clear: "[data-clear-search]",
      popularSearchItems: ["[data-ps-item]"],
      moreResultIcon: "[data-more-result-icon]",
    };

    this.container = document.querySelector("[data-search-popup]");

    this.domNodes = queryDomNodes(this.selectors, this.container);

    this.cachedResults = {};
    this.transitionDuration = 300;
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.searchPopupSearults = this.querySelector(".m-search-popup__result");
    this.popularSearches = this.querySelector("[data-popular-searches]");
    this.searchCount = this.querySelector("[data-search-count]");

    this.setupEventListeners();
  }

  setPopularSearchesLink = () => {
    const { popularSearchItems } = this.domNodes;

    popularSearchItems.forEach(
      (itm) => (itm.href = createSearchLink(itm.dataset.psQuery))
    );
  };

  setupEventListeners() {
    const { clear } = this.domNodes;
    const form = this.querySelector("form.m-search-form");

    if (form) {
      form.addEventListener("submit", this.onFormSubmit.bind(this));
    }

    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this)
    );

    if (clear) {
      clear.addEventListener("click", this.onClearSearch.bind(this));
    }

    MinimogEvents.subscribe(
      MinimogTheme.pubSubEvents.closeSearchPopup,
      () => this.close(true)
    );
  }

  getQuery() {
    return this.input.value.trim();
  }

  /*
   * Optimierte Suche nach Artikelnummern.
   *
   * Beispiele:
   * F130960   -> 130960
   * f130960   -> 130960
   * F-130960  -> 130960
   * F 130960  -> 130960
   * AB130960  -> 130960
   *
   * Normale Suchbegriffe wie:
   * Kreuz
   * Gold Kreuz
   * Anhänger
   *
   * bleiben unverändert.
   */
  normalizeSearchTerm(searchTerm) {
    const originalTerm = searchTerm.trim();

    if (!originalTerm.length) {
      return originalTerm;
    }

    // Leerzeichen und Bindestriche nur für die Prüfung der Artikelnummer entfernen
    const compactTerm = originalTerm.replace(/[\s-]+/g, "");

    // Format prüfen: Buchstaben gefolgt von Zahlen
    const skuMatch = compactTerm.match(/^[a-zA-Z]+(\d+)$/);

    if (skuMatch) {
      return skuMatch[1];
    }

    return originalTerm;
  }

  /*
   * Suchoptionen für die normale Shopify-Suche sicherstellen.
   * Dadurch wird die Artikelnummer / SKU auch auf der Ergebnisseite berücksichtigt.
   */
  addSearchOptionsToForm(form) {
    const searchOptions = {
      "options[unavailable_products]": "last",
      "options[prefix]": "last",
      "options[fields]":
        "title,vendor,product_type,variants.title,variants.sku",
    };

    Object.entries(searchOptions).forEach(([name, value]) => {
      let field = form.querySelector(`input[name="${name}"]`);

      if (!field) {
        field = document.createElement("input");
        field.type = "hidden";
        field.name = name;
        form.appendChild(field);
      }

      field.value = value;
    });
  }

  /*
   * Links zur vollständigen Suchergebnisseite werden ebenfalls angepasst.
   * Das betrifft zum Beispiel den Link "Ergebnisse für ...".
   */
  enhanceSearchLinks() {
    const searchLinks = this.querySelectorAll('a[href*="/search"]');

    searchLinks.forEach((link) => {
      try {
        const url = new URL(link.href, window.location.origin);

        const query = url.searchParams.get("q");

        if (!query) {
          return;
        }

        const normalizedQuery = this.normalizeSearchTerm(query);

        url.searchParams.set("q", normalizedQuery);
        url.searchParams.set("options[unavailable_products]", "last");
        url.searchParams.set("options[prefix]", "last");
        url.searchParams.set(
          "options[fields]",
          "title,vendor,product_type,variants.title,variants.sku"
        );

        link.href = url.toString();
      } catch (error) {
        console.error("Suchlink konnte nicht angepasst werden:", error);
      }
    });
  }

  onChange() {
    const originalSearchTerm = this.getQuery();

    if (!originalSearchTerm.length) {
      this.close(true);
      return;
    }

    const searchTerm = this.normalizeSearchTerm(originalSearchTerm);

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    const originalSearchTerm = this.getQuery();

    if (
      !originalSearchTerm.length ||
      this.querySelector('[aria-selected="true"] a')
    ) {
      event.preventDefault();
      return;
    }

    const form = event.currentTarget;

    /*
     * Die ursprüngliche Eingabe wird vor dem Absenden normalisiert.
     *
     * Beispiel:
     * F130960 -> 130960
     */
    const normalizedSearchTerm =
      this.normalizeSearchTerm(originalSearchTerm);

    this.input.value = normalizedSearchTerm;

    /*
     * SKU als zusätzliches Suchfeld an Shopify übergeben.
     */
    this.addSearchOptionsToForm(form);
  }

  onClearSearch(event) {
    event.preventDefault();

    this.input.value = "";
    this.onChange();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm
      .replace(/\s+/g, "-")
      .toLowerCase();

    this.toggleSpinnerLoading(true);
    this.toggleClearSearch(false);

    const searchByTag = this.dataset.searchByTag === "true";
    const searchByBody = this.dataset.searchByBody === "true";
    const unavailableProductsOption =
      this.dataset.unavailableProductsOption;

    /*
     * Die Artikelnummer / SKU wird in der Predictive Search berücksichtigt.
     */
    let searchFields =
      "title,product_type,vendor,variants.sku,variants.title";

    if (searchByTag) {
      searchFields += ",body";
    }

    if (searchByBody) {
      searchFields += ",tag";
    }

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    /*
     * Es werden gezielt Produkt-Ergebnisse angefordert.
     * Dadurch liefert Shopify bei der SKU-Suche direkt Produktkarten zurück.
     */
    const searchURL =
      `${window.MinimogSettings.routes.predictive_search_url}` +
      `?q=${encodeURIComponent(searchTerm)}` +
      `&resources[type]=product` +
      `&resources[limit]=10` +
      `&resources[options][unavailable_products]=${unavailableProductsOption}` +
      `&resources[options][fields]=${searchFields}` +
      `&section_id=predictive-search`;

    fetch(searchURL)
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status);

          this.close();

          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const parsedDocument = new DOMParser().parseFromString(
          text,
          "text/html"
        );

        const predictiveSection = parsedDocument.querySelector(
          "#shopify-section-predictive-search"
        );

        if (!predictiveSection) {
          this.close();
          return;
        }

        const resultsMarkup = predictiveSection.innerHTML;

        this.cachedResults[queryKey] = resultsMarkup;

        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.close();

        console.error("Predictive Search Fehler:", error);
      });
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;

    this.setAttribute("results", true);

    /*
     * Alle Links zur vollständigen Suchseite werden nach dem Laden
     * der Predictive-Search-Ergebnisse automatisch angepasst.
     */
    this.enhanceSearchLinks();

    const searchItemsWrapper = this.querySelector(
      "[data-search-items-wrapper]"
    );

    if (
      searchItemsWrapper &&
      searchItemsWrapper.childElementCount > 0
    ) {
      this.renderSearchQueryAndMessage(true);
    } else {
      this.renderSearchQueryAndMessage(false);
    }

    this.toggleSpinnerLoading(false);
    this.toggleClearSearch(true);

    this.open();
  }

  renderSearchQueryAndMessage(results) {
    const {
      input,
      searchQuery,
      searchMessage,
      moreResultIcon,
    } = this.domNodes;

    /*
     * Im Suchfeld bleibt während der Predictive Search
     * die Eingabe des Kunden sichtbar.
     */
    const query = input.value;

    const { resultsTitle } = searchMessage.dataset;

    searchQuery.textContent = query;

    this.predictiveSearchResults.classList.remove("m:hidden");
    this.searchPopupSearults.classList.remove("m:hidden");

    if (results) {
      searchMessage.textContent = resultsTitle;

      if (moreResultIcon) {
        moreResultIcon.classList.add("m:hidden");
      }
    } else {
      searchMessage.textContent =
        searchMessage.dataset.resultsTitle;

      if (moreResultIcon) {
        moreResultIcon.classList.remove("m:hidden");
      }
    }
  }

  toggleSpinnerLoading(show) {
    const { loading, submit } = this.domNodes;

    if (submit) {
      submit.style.visibility = show
        ? "hidden"
        : "visible";
    }

    if (loading) {
      loading.style.visibility = show
        ? "visible"
        : "hidden";
    }
  }

  toggleClearSearch(show) {
    const { clear } = this.domNodes;

    if (clear) {
      clear.style.visibility = show
        ? "visible"
        : "hidden";
    }
  }

  open() {
    this.searchPopupSearults.style.setProperty(
      "--search-result-max-height",
      `${
        this.resultsMaxHeight ||
        this.getResultsMaxHeight()
      }px`
    );

    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;

    if (this.popularSearches) {
      this.popularSearches.classList.add("m:hidden");
    }

    if (this.searchCount) {
      this.searchCount.classList.remove("m:hidden");
    }
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
      this.toggleClearSearch(false);
      this.resultsMaxHeight = false;
    }

    this.removeAttribute("open");

    this.predictiveSearchResults.classList.add("m:hidden");
    this.searchPopupSearults.classList.add("m:hidden");

    if (this.popularSearches) {
      this.popularSearches.classList.remove("m:hidden");
    }

    if (this.searchCount) {
      this.searchCount.classList.add("m:hidden");
    }
  }

  getResultsMaxHeight() {
    const predictiveSearch =
      document.querySelector("predictive-search");

    if (!predictiveSearch) {
      return window.innerHeight;
    }

    this.resultsMaxHeight =
      window.innerHeight -
      predictiveSearch.getBoundingClientRect().bottom;

    return this.resultsMaxHeight;
  }
}

customElements.define(
  "predictive-search",
  PredictiveSearch
);